import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// components
import { Modal } from 'components/UI'
import Header from 'components/UI/Modal/Header'
import SaveFilterForm, { AddFilterFormData } from './components/Form'
// hooks
import { useModalData } from 'hooks/index'
// constants
import { Modals } from 'constants/modals'
// graphql
import { RISK_VIEW_FILTER_ADD_MUTATION, RISK_VIEW_FILTER_UPDATE_MUTATION } from 'graphql/risk/mutations'
// types
import { FilterTarget } from 'types/graphql'
// context
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'

const SaveFilterModal: React.FC = () => {
  const [addFilter] = useMutation(RISK_VIEW_FILTER_ADD_MUTATION)
  const [updateFilterMutation] = useMutation(RISK_VIEW_FILTER_UPDATE_MUTATION)

  const { riskView } = React.useContext(RiskRegisterContext)

  const { order, setCurrentTab, setAppliedFilters, filtersRefetch } = React.useContext(RiskViewAPIContext)

  const {
    hideModal,
    modalData: { isOpen, width, fieldFilters, targetId, filter }
  } = useModalData({ modalType: Modals.RISK_VIEW_ADD_FILTER_MODAL })

  const { hideModal: hideFilterDrawer } = useModalData({ modalType: Modals.RISK_VIEW_FILTER_MODAL })

  const isEdit = Boolean(filter)

  const createFilter = async (data: AddFilterFormData) => {
    const args = { global: riskView.isLinkedView ? true : data.global, fieldFilters, order }

    const filterId = get(
      await addFilter({
        variables: {
          input: {
            args,
            name: data.name,
            target: FilterTarget.RiskTemplate,
            ...(riskView.isLinkedView ? { riskViewId: riskView.id } : { targetId })
          }
        }
      }),
      'data.filterCreate.id'
    )

    filtersRefetch()

    if (filterId) {
      setCurrentTab(filterId)
      setAppliedFilters(fieldFilters, filterId)
    }
  }

  const updateFilter = async (data: AddFilterFormData) => {
    const args = { ...filter.args, global: data.global }

    await updateFilterMutation({
      variables: {
        input: {
          args,
          id: filter.id,
          name: data.name
        }
      }
    })

    filtersRefetch()
  }

  const onFormSubmit = async (data: AddFilterFormData) => {
    try {
      if (isEdit) {
        await updateFilter(data)
      } else {
        await createFilter(data)
      }

      hideModal()
      hideFilterDrawer()
    } catch (error) {
      handleError(error, 'SaveFilterModal.onFormSubmit')
    }
  }

  const initialValues = isEdit
    ? {
        name: filter.name,
        global: filter.args.global
      }
    : {
        global: false
      }

  return (
    <Modal hideModal={hideModal} isOpen={isOpen} width={width} PaperProps={{ style: { width: '554px' } }}>
      <Header handleClearClick={hideModal}>Save Filter</Header>
      <SaveFilterForm onSubmit={onFormSubmit} initialValues={initialValues} />
    </Modal>
  )
}

export default SaveFilterModal
